import { Button, Card, CardContent, CardFooter, CardHeader, Spinner, Textarea } from '@ibsys/kit';
import { ArrowLeftIcon, SendIcon } from 'lucide-react';
import { FC, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { AiAgentMessage } from '@/entities/ai-agent-chat';
import { cn } from '@/shared/lib';
import { Chat, ChatMessage, ChatMessageCloud, ChatMessageContent, ChatMessageTitle } from '@/shared/ui/Chat';
import { StreamingText } from '@/shared/ui/StreamingText';
import { MessageBubble } from './MessageBubble';

const HELLO_MESSAGE = `**Добро пожаловать! Я могу:**

- подсказать, как пользоваться функциями системы
- помочь оптимизировать ваши данные
- проанализировать таблицы, отчёты или показатели
- настроить рабочие процессы

**С чем помочь прямо сейчас?**`;

type Props = {
  className?: string;
  messages: AiAgentMessage[];
  isGenerating: boolean;
  hasReplyFailed: boolean;
  isCreating: boolean;
  isInputDisabled: boolean;
  streamingTokens: string[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  onBackToList: () => void;
};

export const CopilotChatPresenter: FC<Props> = ({
  className,
  messages,
  isGenerating,
  hasReplyFailed,
  isCreating,
  isInputDisabled,
  streamingTokens,
  inputValue,
  onInputChange,
  onKeyDown,
  onSend,
  onBackToList,
}) => {
  const { t } = useTranslation(['aiAgent']);

  const hasMessages = messages.length > 0 || isGenerating;

  return (
    <Card className={cn('bg-transparent border-none shadow-none h-full flex flex-col overflow-hidden', className)}>
      <CardHeader className="flex items-center gap-1 px-2 py-1 shrink-0">
        <Button variant="ghost" size="sm" icon={<ArrowLeftIcon strokeWidth={1.25} />} onClick={onBackToList}>
          {t('aiAgent:backToList')}
        </Button>
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden px-0">
        <Chat>
          {!hasMessages && (
            <MessageBubble
              message={{ content: HELLO_MESSAGE, id: -1, role: 'ASSISTANT', createdAt: new Date().toISOString() }}
              isLast={!hasMessages}
            />
          )}
          {messages.map((msg, index) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              isLast={index === messages.length - 1 && !isGenerating && !hasReplyFailed}
            />
          ))}
          {isGenerating && (
            <ChatMessage side="left" isLast>
              <ChatMessageCloud>
                <ChatMessageTitle>{t('aiAgent:title')}</ChatMessageTitle>
                <ChatMessageContent>
                  {streamingTokens.length > 0 ? (
                    <StreamingText tokens={streamingTokens} />
                  ) : (
                    <span className="text-muted-foreground">{t('aiAgent:aiTyping')}</span>
                  )}
                </ChatMessageContent>
              </ChatMessageCloud>
            </ChatMessage>
          )}
          {hasReplyFailed && (
            <ChatMessage side="left" isLast>
              <ChatMessageCloud>
                <ChatMessageTitle>{t('aiAgent:title')}</ChatMessageTitle>
                <ChatMessageContent>
                  <span className="text-destructive">{t('aiAgent:replyFailed')}</span>
                </ChatMessageContent>
              </ChatMessageCloud>
            </ChatMessage>
          )}
        </Chat>
      </CardContent>

      <CardFooter className="p-2">
        {isCreating ? (
          <div className="flex items-center justify-center w-full py-2">
            <Spinner className="size-4" />
          </div>
        ) : (
          <Textarea
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('aiAgent:messagePlaceholder')}
            disabled={isInputDisabled}
            iconRight={
              <Button
                className="size-7"
                icon={<SendIcon strokeWidth={1.25} />}
                variant="ghost"
                onClick={onSend}
                disabled={isInputDisabled || !inputValue.trim()}
              />
            }
          />
        )}
      </CardFooter>
    </Card>
  );
};
